// The `echo` structure symbol set: card 07 of mockup round 054, "SONAR
// ECHO" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// Every object is a sonar return inside one faint scope ring, and an operator
// learns each kind by its echo: the Relay Spire is one hard point with three
// rings, the Forge Vent a rising column of blobs, the Kelp Terrace three
// stacked bars, the Deep Battery one heavy bar with a hot end and a ghost,
// the Trench Throne one large return with two rings, the Drydock Shoal a
// thick crescent with a hull in it, the Archive Vault one dense square, the
// Marshal Court three nested arcs over a floor, and the Coral Ward a ring of
// speckles.
//
// The contested tell is a second return in the attacker's ink, which overlaps
// the scope from the right and carries its own ring. The card also passes a
// sweep line over a contested object. The atlas bakes one still cell, so this
// file draws NO sweep, and the second return alone is the tell (R1104). The
// card itself says the second return is the tell at motion off.
//
// The card's rising column and its expanding rings move with the clock. Each
// one is drawn here at the phase the card shows at motion off.
//
// Every offset, radius, width and alpha below is the figure card 07 draws.

import type { MarkGraphics } from '../marks';
import {
  arcPath, disc, line, ring, roundRectPoints, WATER,
  setOf, type StructureDraw, type StructureInk, type StructureKind,
  type StructureSet,
} from './shared';

/** The scope ring every kind of the family sits in. */
const SCOPE_R = 21;

/**
 * The speckles of the Coral Ward, frozen: x, y and radius of each.
 *
 * The card builds them from `mulberry32(31)`. The atlas bakes one still cell
 * per key, so the ten speckles are stated here rather than rolled again.
 */
const WARD_SPECKLES = [
  12.86, 3.55, 2.15, 9.3, 7.04, 1.91, 3.85, 13.18, 2.19,
  -6.18, 9.44, 2.14, -10.77, 4.54, 2.13, -12.85, -0.24, 2.16,
  -6.33, -9.16, 2.23, 0.25, -13.09, 2.18, 8.1, -9.68, 2.08,
  8.74, -6.16, 1.81,
];

/** A soft return: the halo, the shoulder and the hard core of one echo. */
function blob(
  g: MarkGraphics, x: number, y: number, r: number, color: number, k: number,
): void {
  disc(g, x, y, r * 1.7, color, 0.14 * k);
  disc(g, x, y, r * 1.25, color, 0.3 * k);
  disc(g, x, y, r, color, 0.85 * k);
}

/** A bar return: the soft skirt under the hard line. */
function bar(
  g: MarkGraphics, x0: number, y0: number, x1: number, y1: number,
  w: number, color: number, k: number,
): void {
  line(g, x0, y0, x1, y1, color, 0.18 * k, w + 4);
  line(g, x0, y0, x1, y1, color, 0.8 * k, w);
}

/** An arc return: the soft skirt under the hard arc. */
function arcEcho(
  g: MarkGraphics, r: number, a0: number, a1: number,
  w: number, color: number, k: number,
): void {
  arcPath(g, 0, 0, r, a0, a1);
  g.stroke({ width: w + 3, color, alpha: 0.16 * k, cap: 'round' });
  arcPath(g, 0, 0, r, a0, a1);
  g.stroke({ width: w, color, alpha: 0.8 * k, cap: 'round' });
}

/** One kind of the `echo` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  return (g: MarkGraphics, ink: StructureInk): void => {
    const col = ink.own;
    const a = ink.held ? 1 : 0.7;
    // The scope ring of the family.
    ring(g, 0, 0, SCOPE_R, col, 0.2 * a, 1);
    switch (kind) {
      case 'spire':
        // One hard point, with three rings out of it. The card sends them
        // outward with the clock; at motion off they stand at 7, 13 and 19.
        blob(g, 0, 0, 2.6, col, a);
        for (let k = 0; k < 3; k++) {
          const r = 7 + k * 6;
          ring(g, 0, 0, r, col, (0.7 - (r - 3) / 30) * a, 1.2);
        }
        break;
      case 'forge':
        // The mouth, and the column of blobs that rises out of it. The card
        // lifts the column with the clock; the still cell is phase 0.
        blob(g, 0, 9, 5, col, a);
        blob(g, 1, 0, 3.4, col, a);
        blob(g, -1, -7, 2.5, col, a);
        blob(g, 1, -13, 1.8, col, a);
        break;
      case 'kelp':
        // Three stacked beds, each shorter than the one under it.
        bar(g, -11, -8, 11, -8, 3.5, col, a);
        bar(g, -8, 0, 8, 0, 3.5, col, a);
        bar(g, -5, 8, 5, 8, 3.5, col, a);
        break;
      case 'battery':
        // The heavy tube with the hot muzzle, over its own ghost.
        bar(g, -12, -2, 12, -2, 4.5, col, a);
        disc(g, 11, -2, 3, col, a);
        bar(g, -9, 6, 9, 6, 2, col, 0.4 * a);
        break;
      case 'throne':
        // The largest return of the set, inside two rings.
        blob(g, 0, 0, 8, col, a);
        disc(g, 0, 0, 3.5, col, a);
        ring(g, 0, 0, 14, col, 0.6 * a, 1.6);
        ring(g, 0, 0, 19, col, 0.4 * a, 1);
        break;
      case 'drydock':
        // The cradle as a thick crescent, with the hull inside it.
        arcEcho(g, 11, Math.PI * 0.12, Math.PI * 0.88, 4, col, a);
        blob(g, 0, 2, 3, col, a);
        break;
      case 'archive': {
        // One dense square, knocked out at its centre.
        g.poly(roundRectPoints(-9, -9, 18, 18, 3)).fill({ color: col, alpha: 0.16 * a });
        g.poly(roundRectPoints(-7, -7, 14, 14, 2)).fill({ color: col, alpha: 0.8 * a });
        disc(g, 0, 0, 2, WATER, 0.9);
        break;
      }
      case 'marshal':
        // Three nested arcs over the floor of the hall.
        arcEcho(g, 7, -Math.PI * 0.86, -Math.PI * 0.14, 2.2, col, a);
        arcEcho(g, 13, -Math.PI * 0.86, -Math.PI * 0.14, 2, col, 0.75 * a);
        arcEcho(g, 19, -Math.PI * 0.86, -Math.PI * 0.14, 1.6, col, 0.5 * a);
        bar(g, -18, 3, 18, 3, 2, col, a);
        break;
      default:
        // The Coral Ward: a ring of speckles around one small return.
        for (let k = 0; k < WARD_SPECKLES.length; k += 3) {
          blob(g, WARD_SPECKLES[k], WARD_SPECKLES[k + 1], WARD_SPECKLES[k + 2], col, a);
        }
        blob(g, 0, 0, 2.4, col, a);
        break;
    }
    if (ink.foe !== 0) {
      // The second return, in the attacker's ink. It is the whole tell,
      // because the atlas bakes no sweep (R1104).
      blob(g, 11, 3, 5, ink.foe, 1);
      ring(g, 11, 3, 9, ink.foe, 0.7, 1.4);
    }
  };
}

/** Card 07, all nine kinds. */
export const ECHO_SET: StructureSet = setOf(markOf);
